import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Divider, Button } from "@mui/material";
import { getQTOElementsApi, updateQTOQuantityApi } from "../../../api/qto";
import { formatNumberWithCommas } from "../../../utils/numberFormatter.js";
import { NotificationContext } from "../../../component/popup/NotificationProvider";
import Popup from "../../../component/popup/Popup.js";
import Table from "../../../component/table/MainTable.js";
import FullScreenLoading from "../../../component/FullScreenLoading.js";

const QTOElementsPopup = ({ open, onClose, qtoData, onUpdateComplete }) => {
    const { projectId } = useParams();
    const { showSuccess, showError } = useContext(NotificationContext);
    const tableHead = [
        { id: "itemName", label: "Item Name", align: "left", defaultSortOrder: "asc" },
        { id: "taskID", label: "Task ID", align: "left" },
        { id: "baseQuantity", label: "Base Quantity", align: "left", type: "number", defaultSortOrder: "asc" },
    ];
    const [tableRows, setTableRows] = useState(null);
    const [tablePage, setTablePage] = useState(0);
    const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const [updateMode, setUpdateMode] = useState(false);
    const [originalData, setOriginalData] = useState([]);
    const [modifiedRows, setModifiedRows] = useState({});
    const [showUpdate, setShowUpdate] = useState(false);

    const getTableData = useCallback(async () => {
        if (!projectId || !qtoData?.qtoId) return;
        setIsLoading(true);
        setTablePage(0);
        setTableRowsPerPage(10);

        try {
            const encodedQtoId = encodeURIComponent(qtoData?.qtoId); // handle +、%、&
            const result = await getQTOElementsApi(projectId, encodedQtoId);
            const data = result?.data;
            if (!data || !Array.isArray(data)) {
                setTableRows([]);
                setIsLoading(false);
                return;
            }
            const processedData = data.map((item) => ({
                ...item,
                id: item.elementId,
                baseQuantity: formatNumberWithCommas(item.baseQuantity, 2),
            }));

            setTableRows(processedData);
            setOriginalData(processedData);
        } catch (error) {
            setTableRows([]);
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    }, [projectId, qtoData?.qtoId]);

    const saveColumnData = async () => {
        const modifiedData = Object.values(modifiedRows);
        setIsLoading(true);
        try {
            const encodedQtoId = encodeURIComponent(qtoData?.qtoId); // handle +、%、&
            const result = await updateQTOQuantityApi(projectId, encodedQtoId, modifiedData);
            if (result?.message) {
                showSuccess("Update successful.");
                setModifiedRows({});
                setUpdateMode(false);
                getTableData();
                if (onUpdateComplete) {
                    onUpdateComplete();
                }
            }
        } catch (error) {
            if (error.status === 500 && error.response && error.response.data.detail) {
                showError(error.response.data.detail);
            } else {
                showError("Update failed.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const toggleUpdate = () => {
        setUpdateMode(!updateMode);
    };

    useEffect(() => {
        if (open) {
            setModifiedRows({});
            setUpdateMode(false);
            getTableData();
        }
    }, [open, getTableData]);

    useEffect(() => {
        const unit = qtoData?.unit;
        if (open) {
            const hideOnTheseUnits = ["length", "no's", "volume"];
            setShowUpdate(!hideOnTheseUnits.includes(unit));
        } else {
            setShowUpdate(false);
        }
    }, [open, qtoData?.unit]);

    return (
        <Popup
            open={open}
            onClose={onClose}
            popupStyle={{
                minWidth: 650,
                maxWidth: 650,
            }}
            title="List of Items"
        >
            <Box sx={{ mx: 2, my: 2, backgroundColor: "#F5F5F5", borderRadius: "10px" }}>
                <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <img src="/assets/icons/greylist.svg" alt="list" width={16} height={16} />
                        <Typography sx={{ ml: 1, fontSize: "14px", fontWeight: 600 }}>{qtoData?.qtoId + " - " + qtoData?.qtoItemName}</Typography>
                    </Box>
                    {showUpdate && (<Box>
                        <Button onClick={updateMode ? saveColumnData : toggleUpdate} variant="contained" color="success" sx={{ py: "2px" }}>
                            {updateMode ? "Save" : "Update"}
                        </Button>
                        {updateMode && (
                            <Button
                                onClick={() => {
                                    setTableRows(originalData);
                                    setUpdateMode(false);
                                    setModifiedRows({});
                                }}
                                variant="outlined"
                                color="inherit"
                                sx={{ py: "2px", ml: 1 }}
                            >
                                Cancel
                            </Button>
                        )}
                    </Box>
                    )}
                </Box>
                <Divider />
                <Box
                    sx={{
                        py: 2,
                        px: 2,
                        maxHeight: "calc(100vh - 160px)",
                        overflow: "auto",
                    }}
                >
                    <Table
                        columns={tableHead}
                        rows={tableRows}
                        headerStyles={{
                            default: { backgroundColor: "#D0D5DE" },
                        }}
                        sx={{ maxHeight: 275 }}
                        sortable
                        sortableColumns={["itemName", "baseQuantity"]}
                        showPagination
                        page={tablePage}
                        rowsPerPage={tableRowsPerPage}
                        onPageChange={(newPage) => setTablePage(newPage)}
                        onRowsPerPageChange={(newRowsPerPage) => setTableRowsPerPage(newRowsPerPage)}
                        emptyText="No Item available"
                        updateMode={updateMode}
                        editableColumns={["baseQuantity"]}
                        onCellChange={(rowId, columnId, newValue) => {
                            setTableRows((prevRows) => {
                                const newRows = prevRows.map((row) =>
                                    row.id === rowId
                                        ? {
                                            ...row,
                                            [columnId]: newValue,
                                        }
                                        : row
                                );
                                const updatedRow = newRows.find((r) => r.id === rowId);
                                const originalRow = originalData.find((r) => r.id === rowId);
                                const hasChanges = Object.keys(updatedRow).some((key) => updatedRow[key] !== originalRow?.[key]);

                                if (hasChanges) {
                                    setModifiedRows((prev) => ({
                                        ...prev,
                                        [rowId]: updatedRow,
                                    }));
                                } else {
                                    setModifiedRows((prev) => {
                                        const next = { ...prev };
                                        delete next[rowId];
                                        return next;
                                    });
                                }

                                return newRows;
                            });
                        }}
                    />
                </Box>
            </Box>
            <Box textAlign="center">
                <Button variant="outlined" color="inherit" onClick={onClose} sx={{ borderRadius: "10px", lineHeight: "normal", mb: 2 }}>
                    Back
                </Button>
            </Box>
            <FullScreenLoading isLoading={isLoading} />
        </Popup>
    );
};

export default QTOElementsPopup;
